// Who may open which raisedbeef.com page.
//
// ── THE RULE (owner, 2026-10-09; gate built 2026-10-12; ONE PLAN 2026-10-25) ─
// FREE, never asked for anything: the homepage Cattle Ticker and Boxed Beef,
// and — since 2026-10-16 — the four leaderboards (steers, heifers, the Calf
// Tracker, every barn's own board), because a shared board must open for the
// person it was shared with. Every other tool page asks for an account, opens
// everything for 30 days, and then needs THE plan (src/lib/sitePlan.ts).
// There used to be two tiers, Basic and Premium, and each page carried the
// one it needed; since 2026-10-25 there is one plan and a page is simply
// gated or free. Plan in RAISEDBEEF_COM_BILLING_PLAN.md §0. The level itself
// comes from the database (rb_site_level), never from here.
//
// ── WHAT THIS FILE IS NOT ────────────────────────────────────────────────────
// It is not security. It is the front door. The real lock on the indexes is
// server-side (plan §5, step 3): a reader that refuses a caller below Premium.

/** Paths that ask for an account. Exact matches and one pattern each. */
/**
 * The gated pages, each with the name the card calls it.
 *
 * ⚠️ THE NAME IS WHY THIS IS A LIST OF PAIRS, NOT A LIST OF PATHS. The card
 * names the tool the person just tapped — "Sale Reports opens for 30 days…" —
 * and then covers everything else generically, so it stays specific without
 * being rewritten every time a tool is added (owner, 2026-09-16: "I'll be
 * adding a lot more tools in the future"). Adding a tool is one line here: the
 * path it is gated on, and what to call it.
 */
const GATED: { match: RegExp; name: string }[] = [
  { match: /^\/reports\/?$/, name: "Sale Reports" },
  // ⚠️ THE LEADERBOARDS ARE FREE, ON PURPOSE (owner, 2026-10-16: "leaderboards
  // should be free for everyone so we can share without them hitting a sign in
  // wall"). /leaderboard, /leaderboard/heifers, /leaderboard/calves (and its
  // old address /calf-tracker/national, which redirects there) and every
  // barn's /barn/<slug>/leaderboard were Basic from 2026-10-12 to 2026-10-16 and
  // are deliberately NOT on this list now: a shared board is the product's
  // front door, and a shared link that lands on a sign-in card is a dead share.
  // Do not re-add them by reading the "barn pages are Basic" rule below alone.
  { match: /^\/steer-tracker\/?$/, name: "The Steer Tracker" },
  { match: /^\/(4|5|6|7|8|9|10)-wt-map\/?$/, name: "$1 Wt Map" },
  { match: /^\/diesel\/?$/, name: "Diesel" },
  { match: /^\/receipts\/?$/, name: "Steer & Heifer Receipts" },
  { match: /^\/packer-margin\/?$/, name: "Packer Margin" },
  { match: /^\/cattle-on-feed\/?$/, name: "Cattle on Feed" },
  { match: /^\/border\/?$/, name: "The Border" },
  { match: /^\/flash(\/.*)?$/, name: "The Flash screen" },
  { match: /^\/fat-cattle\/?$/, name: "Fat Cattle Sales" },
  // ⚠️ GATED ON THE OWNER'S CALL, 2026-10-12, AGAINST THE SOURCE RULE ABOVE.
  // Fed Cattle was free as a USDA page. It became the one page for fed cattle —
  // USDA, plus the barns selling fats today, their live lots and the weekly
  // barn price — and the owner put it behind sign-in: "this is behind the
  // sign in". Do not move it back to free by reading the source rule alone.
  { match: /^\/fed-cattle\/?$/, name: "Fed Cattle" },
  // ⚠️ GATED ON THE OWNER'S CALL, 2026-10-02, AGAINST THE SOURCE RULE ABOVE.
  // By source Clara Light belongs on the free side — it is boxed beef, built
  // on USDA's published report. It is gated because of the other half of the
  // same sentence: "what a subscription buys is the work on top", and the
  // commentary IS the work on top. Asked and answered rather than assumed; do
  // not "correct" this to free by reading the source rule alone.
  // ⚠️ /clara, NOT /clara-light, AS OF 2026-10-03 — the door moved when the
  // owner retired the old Clara app and freed the path. The old path redirects
  // and is deliberately NOT listed here: gating a redirect would show the
  // account card, then redirect, then show it again.
  { match: /^\/clara\/?$/, name: "Clara" },
  { match: /^\/fat-cattle\/[^/]+\/?$/, name: "This barn's fat cattle sale" },
  { match: /^\/barn\/[^/]+\/live\/?$/, name: "This barn's sale report" },
  // ⚠️ GATED FOR A SECURITY REASON, NOT AN EDITORIAL ONE — the only entry here
  // that is. This page was the ONLY anonymous caller of `rpc_dv_daily_summary`,
  // which takes a caller-set row limit with no ceiling and a caller-set date
  // window with no floor, and so will hand a stranger the whole of
  // `dv_daily_cohort_stats` — 251,283 current rows — day by day, without ever
  // touching the table. Closing the table does nothing while that is open.
  //
  // The function's EXECUTE is revoked from `anon` in migration
  // 20261003000002. THE TWO MUST STAY TOGETHER: revoke without this line and a
  // public page shows an empty archive with no error; this line without the
  // revoke gates a page while the door stays open.
  //
  // Costs nothing in search — the sitemap lists /barn/<slug>/ticker and
  // /barn/<slug>/live, never /archive.
  { match: /^\/barn\/[^/]+\/archive\/?$/, name: "This barn's sale archive" },
  // ── Added with the paywall, 2026-10-12 ──
  // The barn and state boards that were free until then.
  { match: /^\/barns\/?$/, name: "The barn list" },
  { match: /^\/states\/?$/, name: "The state list" },
  { match: /^\/ticker\/[^/]+\/?$/, name: "This state's ticker" },
  { match: /^\/barn\/[^/]+\/ticker\/?$/, name: "This barn's ticker" },
  { match: /^\/barn\/[^/]+\/?$/, name: "This barn's page" },
  { match: /^\/toppers\/?$/, name: "Toppers" },
  // The indexes. ⚠️ /index/methodology, /index/reconciliation and
  // the keyed calculation sheets are NOT here. The corrections log IS (owner,
  // 2026-10-13: "Let's put it behind premium") — it prints each correction's
  // old and new price, and the issued links it was waiting for were dropped.
  { match: /^\/index\/?$/, name: "The Cattle Market Central Indexes" },
  { match: /^\/index\/corrections\/?$/, name: "The corrections log" },
  { match: /^\/index\/(?!methodology\/?$|corrections\/?$|reconciliation\/?$)[^/]+\/?$/, name: "This Cattle Market Central Index" },
  { match: /^\/index\/[^/]+\/daily\/[^/]+\/?$/, name: "This day's index" },
  { match: /^\/quotes-board\/?$/, name: "Weight Breaks" },
  { match: /^\/weekly-averages\/?$/, name: "Weekly Averages" },
  // ⚠️ GATED ON THE OWNER'S CALL, 2026-10-12, over the old "never gate the
  // broadcast" note in App.tsx: Corbitt Wall's viewers now meet the wall.
  { match: /^\/sheet\/?$/, name: "The Sale Sheet" },
  { match: /^\/sheet\/[^/]+\/?$/, name: "This Sale Sheet" },
];

/** Does this page ask for an account (and, after the free 30 days, the plan)?
 *  Pure, and the single source of truth — App.tsx wraps the same list of
 *  routes and a test holds the two together. */
export function requiresAccount(pathname: string): boolean {
  return GATED.some((g) => g.match.test(pathname));
}

/** What to call this page on the card. "This page" when we have no better name,
 *  which reads fine in the sentence and is never wrong. */
export function toolName(pathname: string): string {
  for (const g of GATED) {
    const m = g.match.exec(pathname);
    if (m) return g.name.replace("$1", m[1] ?? "");
  }
  return "This page";
}

/**
 * Crawlers are never gated.
 *
 * Google renders JavaScript, so a client-side gate WOULD hide these pages from
 * search if it applied to Googlebot — and the link-preview cards we build for
 * texts and Facebook read the page too. Letting a declared crawler through is
 * the same "flexible sampling" arrangement news sites run under, and it is the
 * difference between a paywall that grows and one that strangles the site.
 */
const CRAWLER = /bot|crawl|spider|slurp|facebookexternalhit|facebot|embedly|quora link preview|showyoubot|outbrain|pinterest|slackbot|vkshare|w3c_validator|whatsapp|telegram|discord|twitter|linkedinbot|applebot|bingpreview|google-inspectiontool|chrome-lighthouse/i;

export function isCrawler(userAgent: string | null | undefined): boolean {
  return !!userAgent && CRAWLER.test(userAgent);
}

/** rb_site_level(uid): the one answer to "what is this person on".
 *  `premium` is the plan (ONE PLAN, 2026-10-25 — a grandfathered Basic
 *  subscription reads as premium too; the database never says "basic"). */
export type Level = "admin" | "premium" | "trial" | "none";

/** What the gate knows about the person in front of it. */
export interface AccessInput {
  /** Still reading the session or the plan. */
  loading: boolean;
  signedIn: boolean;
  /** From rb_site_my_billing; null when the read failed. */
  level: Level | null;
  /** The end of their free 30 days, when they have a clock. */
  trialEndsAt: string | null;
}

export type Access =
  /** Draw the page. */
  | { state: "open"; daysLeft: number | null }
  /** Draw the sign-up card. */
  | { state: "signup" }
  /** Free month over, no plan: draw the plan picker. */
  | { state: "expired" }
  /** Say nothing yet — never flash a wall at someone who is signed in. */
  | { state: "loading" };

/** Whole days left in the trial, never negative. Null when there is no clock.
 *
 *  ⚠️ NO CAP AT 30. It used to be capped, because a visitor's clock a second
 *  behind the server's made a new account read "31 days left" (owner's test,
 *  2026-10-12) — but a free month extended from /manage/subscribers to Dec 31
 *  then read "30 days left" for weeks. Fifteen minutes of slack does the same
 *  job: 30 days and a few seconds still reads 30. */
export function daysLeft(trialEndsAt: string | null | undefined, now: Date = new Date()): number | null {
  if (!trialEndsAt) return null;
  const end = Date.parse(trialEndsAt);
  if (!Number.isFinite(end)) return null;
  return Math.max(0, Math.ceil((end - now.getTime()) / 86_400_000 - 1 / 96));
}

/**
 * The decision.
 *
 * ⚠️ ORDER MATTERS. `loading` comes first: a signed-in person whose plan has
 * not arrived yet must never see a sign-up card for a second — that reads as
 * "you are logged out" and is how people end up registering twice.
 */
export function decideAccess(input: AccessInput, now: Date = new Date()): Access {
  if (input.loading) return { state: "loading" };
  if (!input.signedIn) return { state: "signup" };

  switch (input.level) {
    // The free 30 days open everything.
    case "trial":
      return { state: "open", daysLeft: daysLeft(input.trialEndsAt, now) };
    case "none":
      return { state: "expired" };
    // admin, premium — and a failed read (null): open. A hiccup must never
    // wall off someone who has paid, and this file is the front door, not
    // the lock. An unknown word from an older server answers the same way.
    default:
      return { state: "open", daysLeft: null };
  }
}

/** "27 days left" / "Last day" / "1 day left" — the line on the page. */
export function trialLine(left: number | null): string | null {
  if (left === null) return null;
  if (left <= 0) return "Your free month has ended";
  if (left === 1) return "Last day of your free month";
  return `${left} days left in your free month`;
}

// ── SUBSCRIBE ANY TIME, KEEP THE FREE DAYS (owner, 2026-11-03) ───────────────
// The industry standard for a free month with no card: the Subscribe button is
// in sight every day of it, and buying early never costs the days still left —
// the first charge lands on the day the free month would have ended. Mock
// https://claude.ai/artifact/3fBoVMTbhQsywYnRBth49d, approved 2026-11-03.
//
// ⚠️ STRIPE'S 48 HOURS. Checkout refuses a trial_end less than 48 hours away,
// so in the last two days a purchase is charged that day. rb-site-checkout
// applies the SAME margin (KEEP_FREE_MIN_MS there); the plan card must never
// promise "nothing charged today" when the server will charge today.

/** Free time left that a purchase can carry over: Stripe's 48 hours plus slack for a slow checkout. */
export const KEEP_FREE_MIN_MS = 48 * 3_600_000 + 30 * 60_000;

/** Will subscribing now keep the rest of the free month (first charge on the day it ends)? */
export function keepsFreeDays(trialEndsAt: string | null | undefined, now: Date = new Date()): boolean {
  if (!trialEndsAt) return false;
  const end = Date.parse(trialEndsAt);
  return Number.isFinite(end) && end - now.getTime() >= KEEP_FREE_MIN_MS;
}

/** "Nov 24" — the day the free month ends, on the barns' clock (Central). */
export function freeEndDay(trialEndsAt: string): string {
  return new Date(trialEndsAt).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "America/Chicago" });
}

/** "4:12 PM" Central. */
function freeEndTime(trialEndsAt: string): string {
  return new Date(trialEndsAt).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" });
}

/** The few words beside the header's Subscribe button. */
export function freeMonthBadge(left: number): string {
  return left <= 1 ? "Last free day" : `${left} days free`;
}

/**
 * The strip under the masthead while someone is on the free month: quiet until
 * the last seven days, then gold; the last day says when, to the minute.
 */
export function freeMonthStrip(
  left: number,
  trialEndsAt: string,
  monthly: string,
  now: Date = new Date(),
): { tone: "quiet" | "gold"; text: string; cta: string } {
  if (left <= 1) {
    const sameDay = freeEndDay(trialEndsAt) === freeEndDay(now.toISOString());
    return {
      tone: "gold",
      text: `Last day of your free month — it ends ${sameDay ? "today" : "tomorrow"} at ${freeEndTime(trialEndsAt)} Central`,
      cta: "Subscribe to keep the tickers, indexes and maps",
    };
  }
  if (left <= 7) {
    return {
      tone: "gold",
      text: `${left} days left in your free month — the tools lock ${freeEndDay(trialEndsAt)}`,
      cta: keepsFreeDays(trialEndsAt, now) ? `Keep everything for ${monthly}/month, nothing charged until then` : `Keep everything for ${monthly}/month`,
    };
  }
  return {
    tone: "quiet",
    text: `${left} days left in your free month`,
    cta: `Subscribe now and keep them — first charge ${freeEndDay(trialEndsAt)}`,
  };
}
